import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  // O carrinho é lido no inicializador do useState, não num efeito: se fosse um
  // efeito, o efeito de escrita abaixo corria no mesmo commit ainda com o estado
  // inicial vazio e gravava `[]` por cima do carrinho guardado, esvaziando-o a
  // cada reload.
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error parsing cart from localStorage', error);
      return [];
    }
  });

  // Update localStorage whenever cartItems changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Outros separadores escrevem a mesma chave: o evento `storage` só dispara
  // nas outras tabs, por isso adotar o valor novo aqui não cria ciclo.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== 'cart') return;
      try {
        setCartItems(event.newValue ? JSON.parse(event.newValue) : []);
      } catch (error) {
        console.error('Error parsing cart from storage event', error);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => {
      // Determine the unique ID for the cart item
      // product.id here is the base product ID.
      // If product.variant exists, it means it's a variant being added.
      const cartItemId = product.variant && product.variant.sku
        ? product.variant.sku
        : product.id.toString();

      // Tecto de stock: nunca deixar o carrinho pedir mais do que existe.
      // Produtos sem stock finito (digitais) ficam sem limite — um digital é
      // vendido com `stock: 0`, e o tecto tinha de ser ignorado, não aplicado.
      const availableStock =
        !product.isDigital &&
        product.stock != null &&
        Number.isFinite(Number(product.stock))
          ? Number(product.stock)
          : null;

      const existingItemIndex = prevItems.findIndex(item => item.id === cartItemId);

      if (existingItemIndex !== -1) {
        // Update quantity of existing item
        const updatedItems = [...prevItems];
        const requested = updatedItems[existingItemIndex].quantity + quantity;
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: availableStock !== null ? Math.min(requested, availableStock) : requested
        };
        return updatedItems;
      } else {
        // Add new item to cart
        // If it's a variant, its details (price, name, etc.) are already part of the 'product' object passed
        // from ProductDetailPage, because we did {...product, ...selectedVariant, variant: selectedVariant}
        // However, the 'product' object passed to addToCart from ProductDetailPage when a variant is selected
        // already has its price and stock overridden by the variant's values.
        // The 'variant' property within this product object holds the actual variant details.
        const newItem = {
          id: cartItemId, // This is our unique cart item ID
          productId: product.id, // Original product ID
          name: product.name, // Base product name
          price: product.variant ? product.variant.price : product.price,
          image: product.images && product.images.length > 0 ? product.images[0].src : null,
          quantity: availableStock !== null ? Math.min(quantity, availableStock) : quantity,
          stock: availableStock,
          isDigital: product.isDigital || false,
          // Store the variant object if it exists
          variant: product.variant || null
        };
        return [...prevItems, newItem];
      }
    });
  };

  const updateCartItemQuantity = (cartItemId, quantity) => {
    // NaN e Infinity não são quantidades: `NaN <= 0` é falso e passariam a guarda.
    if (!Number.isFinite(quantity)) return;

    if (quantity <= 0) {
      removeFromCart(cartItemId);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === cartItemId
          ? { ...item, quantity: item.stock != null ? Math.min(quantity, item.stock) : quantity }
          : item
      )
    );
  };

  const removeFromCart = (cartItemId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== cartItemId));
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cart');
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        updateCartItemQuantity,
        removeFromCart,
        clearCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);

export default CartContext;
